import { useContext } from 'react';
import PropTypes from 'prop-types';
import { inject, observer, PropTypes as MobXPropTypes } from 'mobx-react';
import {
  PAYMENT_METHOD,
  RECIPIENT_TYPES,
  NEW_PAYMENT_CREATION_STEPS,
  BACKEND_MODULE_TYPES,
  MAX_UPLOADED_FILES_SIZE
} from 'components/common/constants';
import i18nContext from 'components/i18n-context';
import { getNewPaymentFormStepErrorMessage } from 'components/NewPaymentFormV2/utils/getNewPaymentFormStepErrorMessage';
import { CURRENCIES } from 'services/currenciesConstant';
import {
  amountFormattedValue,
  isCryptoProvider,
  isBeneficiaryStateProvider,
  convertBytesToMegabytes,
  getErrorMessageForAlert
} from 'services/utils';
import { ActionButton } from 'uikit/ActionButton/ActionButton';
import Button from 'uikit/Button/Button';
import { PopUp } from 'uikit/PopUp/PopUp';
import Alert from 'uikit/Alert/Alert';
import CustomFileDragAndDrop from 'components/CustomFileDragAndDrop';
import PopUpInternalActionConfirmationScheme from 'components/common/PopUpScheme/PopUpInternalActionConfirmationScheme';
import PopUpSuccessScheme from 'components/common/PopUpScheme/PopUpSuccessScheme';
import CryptoAddress from 'components/common/CryptoAddress/CryptoAddress';
import './ReviewAndSaveNewPaymentFormStep.scss';

const ReviewAndSaveNewPaymentFormStep = ({ newPaymentStore, customerNumber }) => {
  const i18n = useContext(i18nContext);

  const data = newPaymentStore.getAllStepsData();
  const isBeneficiaryStateAvailable = isBeneficiaryStateProvider(newPaymentStore.currentAccount?.transfer_provider);

  const calculateCommission = async () => {
    try {
      const providerData = {
        amount: data.amount,
        comment: data.transferDetails,
        recipient_wallet: data.accountNumber || data.iban || data.cryptoAddress,
        account_number: data.accountNumber,
        sort_code: data.sortCode,
        recipient_type: data.recipientType,
        company_name: data.companyName,
        first_name: data.firstName,
        last_name: data.lastName,
        registration_number: data.registrationNumber,
        beneficiary_city: data.city,
        beneficiary_country: data.country,
        beneficiary_post_code: data.postCode,
        beneficiary_state: data.state,
        beneficiary_address: data.address,
        bank_country: data.bankCountry,
        bank_name: data.bankName,
        bic: data.bic
      };

      const isCurrentAccountCrypto = isCryptoProvider(newPaymentStore.currentAccount?.transfer_provider);

      if (isCurrentAccountCrypto) {
        await newPaymentStore.validateCryptoTransfer({ customerNumber, providerData });
      } else {
        await newPaymentStore.validateTransfer({ customerNumber, providerData });
      }
    } catch (error) {
      console.error('Error calculating commission:', error);
    }
  };

  const editPaymentDetails = () => {
    newPaymentStore.resetCommissionData();
  };

  const createNewPayment = async () => {
    try {
      const isCurrentAccountCrypto = isCryptoProvider(newPaymentStore.currentAccount?.transfer_provider);

      if (isCurrentAccountCrypto) {
        await newPaymentStore.createCryptoTransfer(customerNumber)();
      } else {
        await newPaymentStore.createTransfer(customerNumber)();
      }
    } catch (error) {
      console.error('Error creating payment:', error);
    }
  };

  const getSectionError = (fields) => {
    if (!newPaymentStore.error) return null;

    if (Array.isArray(newPaymentStore.error.details)) {
      const found = newPaymentStore.error.details.find((item) =>
        fields.some((f) => item.field === f || item.field.split('.').pop() === f)
      );

      if (found) {
        const shortField = fields.find((f) => found.field === f || found.field.split('.').pop() === f) || found.field;

        return `${shortField}: ${getNewPaymentFormStepErrorMessage(i18n, newPaymentStore.error, shortField)}`;
      }
    }

    if (newPaymentStore.error.fields) {
      for (const field of fields) {
        if (newPaymentStore.error.fields[field]) {
          return getNewPaymentFormStepErrorMessage(i18n, newPaymentStore.error, field);
        }
      }
    }

    return null;
  };

  const hasSectionError = (fields) => {
    if (!newPaymentStore.error) return false;

    // Check for INSUFFICIENT_FUNDS error code for amount field
    if (newPaymentStore.error.code === 'INSUFFICIENT_FUNDS' && fields.includes('amount')) {
      return true;
    }

    if (Array.isArray(newPaymentStore.error.details)) {
      return newPaymentStore.error.details.some((item) =>
        fields.some((f) => item.field === f || item.field.split('.').pop() === f)
      );
    }

    if (newPaymentStore.error.fields) {
      return fields.some((field) => newPaymentStore.error.fields[field]);
    }

    return false;
  };

  const verifyPayment = (securityCode) => newPaymentStore.verifyPaymentSecurityCode(customerNumber, securityCode);
  const verifyCryptoTransfer = (securityCode) => newPaymentStore.verifyCryptoSecurityCode(customerNumber, securityCode);
  const resendVerifyCode = () => newPaymentStore.resendPaymentSecurityCode();

  const handleCloseInternalActionConfirmation = () => {
    newPaymentStore.setIsShowingInternalActionConfirmation(false);
    newPaymentStore.setIsShowSwitchConfirmationMethodScreen(false);
  };

  const renderDetailRow = (label, value, valueClassName, isAddress) => {
    if (!value) return null;
    const addressClassName = isAddress ? 'crypto-address-cell' : '';
    return (
      <div className={'new-payment-template-detail-row'}>
        <div className={'new-payment-template-detail-label'}>{label}</div>
        <div className={`new-payment-template-detail-value ${valueClassName || ''} ${addressClassName}`}>
          {isAddress ? <CryptoAddress address={value} /> : value}
        </div>
      </div>
    );
  };

  return (
    <div className={'new-payment-template-confirmation-container'}>
      {newPaymentStore.temporaryError && (
        <Alert
          type={'warning'}
          className={'review-and-save-temporary-error-alert'}
          message={getErrorMessageForAlert(i18n, newPaymentStore.temporaryError)}
        />
      )}
      <div className={'new-payment-template-confirmation-card'}>
        <div className={'new-payment-template-section'}>
          <div className={'template-section-title-wrapper'}>
            <h3
              className={`new-payment-template-section-title ${hasSectionError(['amount', 'transferDetails']) ? 'new-payment-has-error' : ''}`}
            >
              {i18n.getMessage('newPaymentForms.creationConfirmation.step.paymentDetails')}
            </h3>
            {!newPaymentStore.isShowingConfirmationTab && (
              <ActionButton
                tooltipText={i18n.getMessage('newPaymentForms.creationConfirmation.button.edit.tooltip')}
                iconType={'edit-for-approval'}
                onClick={() => newPaymentStore.setCurrentNewPaymentFormStep(NEW_PAYMENT_CREATION_STEPS.PAYMENT_DETAILS)}
              />
            )}
          </div>
          {renderDetailRow(
            i18n.getMessage('newPaymentForms.creationConfirmation.field.amount'),
            //eslint-disable-next-line max-len
            `${CURRENCIES[data.currency]?.symbol || data.currency} ${amountFormattedValue(data.amount, data.isCryptoPayment ? 8 : 2)}`,
            'amount-highlight'
          )}
          {newPaymentStore.commission &&
            renderDetailRow(
              i18n.getMessage('newPaymentForms.creationConfirmation.field.commission'),
              `${CURRENCIES[newPaymentStore.commission.currency]?.symbol || newPaymentStore.commission.currency} ${amountFormattedValue(newPaymentStore.commission.value, 2)}`,
              'amount-highlight'
            )}
          {!data.isCryptoPayment &&
            renderDetailRow(
              i18n.getMessage('newPaymentForms.creationConfirmation.field.transferDetails'),
              data.transferDetails
            )}
          {getSectionError(['amount', 'transferDetails']) && (
            <div className={'new-payment-template-confirmation-error-message'}>
              {getSectionError(['amount', 'transferDetails'])}
            </div>
          )}
        </div>

        <div className={'new-payment-section-divider'} />

        <div className={'new-payment-template-section'}>
          <div className={'template-section-title-wrapper'}>
            <h3
              className={`new-payment-template-section-title ${
                hasSectionError(['accountNumber', 'sortCode', 'iban', 'network']) ? 'new-payment-has-error' : ''
              }`}
            >
              {i18n.getMessage('newPaymentForms.creationConfirmation.step.accountDetails')}
            </h3>
            {!newPaymentStore.isShowingConfirmationTab && (
              <ActionButton
                tooltipText={i18n.getMessage('newPaymentForms.creationConfirmation.button.edit.tooltip')}
                iconType={'edit-for-approval'}
                onClick={() => newPaymentStore.setCurrentNewPaymentFormStep(NEW_PAYMENT_CREATION_STEPS.ACCOUNT_DETAILS)}
              />
            )}
          </div>
          {renderDetailRow(
            i18n.getMessage('newPaymentForms.creationConfirmation.field.accountNumber'),
            data.accountNumber
          )}
          {renderDetailRow(i18n.getMessage('newPaymentForms.creationConfirmation.field.sortCode'), data.sortCode)}
          {renderDetailRow(i18n.getMessage('newPaymentForms.creationConfirmation.field.recipientIban'), data.iban)}
          {renderDetailRow(
            i18n.getMessage('newPaymentForms.creationConfirmation.field.walletAddress'),
            data.cryptoAddress,
            undefined,
            true
          )}
          {getSectionError(['accountNumber', 'sortCode', 'iban', 'network']) && (
            <div className={'new-payment-template-confirmation-error-message'}>
              {getSectionError(['accountNumber', 'sortCode', 'iban', 'network'])}
            </div>
          )}
        </div>

        {!data.isCryptoPayment && <div className={'section-divider'} />}

        {newPaymentStore.newPaymentFormSteps?.includes(NEW_PAYMENT_CREATION_STEPS.BANK_DETAILS) && (
          <>
            <div className={'new-payment-template-section'}>
              <div className={'new-payment-template-section-title-wrapper'}>
                <h3
                  className={`new-payment-template-section-title ${
                    hasSectionError(['bankName', 'bankCountry', 'bic']) ? 'new-payment-has-error' : ''
                  }`}
                >
                  {i18n.getMessage('newPaymentForms.creationConfirmation.step.bankDetails')}
                </h3>
                {!newPaymentStore.isShowingConfirmationTab && (
                  <ActionButton
                    tooltipText={i18n.getMessage('newPaymentForms.creationConfirmation.button.edit.tooltip')}
                    iconType={'edit-for-approval'}
                    onClick={() =>
                      newPaymentStore.setCurrentNewPaymentFormStep(NEW_PAYMENT_CREATION_STEPS.BANK_DETAILS)
                    }
                  />
                )}
              </div>
              {renderDetailRow(
                i18n.getMessage('newPaymentForms.creationConfirmation.field.recipientBicSwift'),
                data.bic
              )}
              {renderDetailRow(i18n.getMessage('newPaymentForms.creationConfirmation.field.bankName'), data.bankName)}
              {renderDetailRow(
                i18n.getMessage('newPaymentForms.creationConfirmation.field.bankCountry'),
                data.bankCountry
              )}
              {renderDetailRow(
                i18n.getMessage('newPaymentForms.creationConfirmation.field.routingCode'),
                data.routingCode
              )}
              {getSectionError(['bankName', 'bankCountry', 'bic']) && (
                <div className={'new-payment-template-confirmation-error-message'}>
                  {getSectionError(['bankName', 'bankCountry', 'bic'])}
                </div>
              )}
            </div>
            <div className={'new-payment-section-divider'} />
          </>
        )}

        {newPaymentStore.newPaymentFormSteps?.includes(NEW_PAYMENT_CREATION_STEPS.RECIPIENT_DETAILS) && (
          <>
            <div className={'new-payment-template-section'}>
              <div className={'new-payment-template-section-title-wrapper'}>
                <h3
                  className={`new-payment-template-section-title ${
                    hasSectionError(['recipientType', 'companyName', 'firstName', 'lastName'])
                      ? 'new-payment-has-error'
                      : ''
                  }`}
                >
                  {i18n.getMessage('newPaymentForms.creationConfirmation.step.recipientDetails')}
                </h3>
                {!newPaymentStore.isShowingConfirmationTab && (
                  <ActionButton
                    tooltipText={i18n.getMessage('newPaymentForms.creationConfirmation.button.edit.tooltip')}
                    iconType={'edit-for-approval'}
                    onClick={() =>
                      newPaymentStore.setCurrentNewPaymentFormStep(NEW_PAYMENT_CREATION_STEPS.RECIPIENT_DETAILS)
                    }
                  />
                )}
              </div>
              {renderDetailRow(
                i18n.getMessage('newPaymentForms.creationConfirmation.field.recipientType'),
                data.recipientType
              )}
              {data.recipientType === RECIPIENT_TYPES.COMPANY &&
                renderDetailRow(
                  i18n.getMessage('newPaymentForms.creationConfirmation.field.recipientCompanyName'),
                  data.companyName
                )}

              {data.recipientType === RECIPIENT_TYPES.INDIVIDUAL && (
                <>
                  {renderDetailRow(
                    i18n.getMessage('newPaymentForms.creationConfirmation.field.recipientFirstName'),
                    data.firstName
                  )}
                  {renderDetailRow(
                    i18n.getMessage('newPaymentForms.creationConfirmation.field.recipientLastName'),
                    data.lastName
                  )}
                </>
              )}
              {getSectionError(['recipientType', 'companyName', 'firstName', 'lastName']) && (
                <div className={'new-payment-template-confirmation-error-message'}>
                  {getSectionError(['recipientType', 'companyName', 'firstName', 'lastName'])}
                </div>
              )}
            </div>

            {data.paymentMethod === PAYMENT_METHOD.SWIFT && <div className={'section-divider'} />}
          </>
        )}

        {newPaymentStore.newPaymentFormSteps?.includes(NEW_PAYMENT_CREATION_STEPS.RECIPIENT_ADDITIONAL_DETAILS) && (
          <>
            <div className={'new-payment-template-section'}>
              <div className={'new-payment-template-section-title-wrapper'}>
                <h3
                  className={`new-payment-template-section-title ${
                    hasSectionError(['registrationNumber', 'country', 'city', 'postCode', 'state', 'address'])
                      ? 'new-payment-has-error'
                      : ''
                  }`}
                >
                  {i18n.getMessage('newPaymentForms.creationConfirmation.step.recipientAdditionalDetails')}
                </h3>
                {!newPaymentStore.isShowingConfirmationTab && (
                  <ActionButton
                    tooltipText={i18n.getMessage('newPaymentForms.creationConfirmation.button.edit.tooltip')}
                    iconType={'edit-for-approval'}
                    onClick={() =>
                      newPaymentStore.setCurrentNewPaymentFormStep(
                        NEW_PAYMENT_CREATION_STEPS.RECIPIENT_ADDITIONAL_DETAILS
                      )
                    }
                  />
                )}
              </div>
              {renderDetailRow(
                i18n.getMessage('newPaymentForms.creationConfirmation.field.registrationNumber'),
                data.registrationNumber
              )}
              {renderDetailRow(i18n.getMessage('newPaymentForms.creationConfirmation.field.recipientCity'), data.city)}
              {renderDetailRow(
                i18n.getMessage('newPaymentForms.creationConfirmation.field.recipientCountry'),
                data.country
              )}
              {renderDetailRow(
                i18n.getMessage('newPaymentForms.creationConfirmation.field.recipientPostalCode'),
                data.postCode
              )}
              {isBeneficiaryStateAvailable &&
                renderDetailRow(
                  i18n.getMessage('newPaymentForms.creationConfirmation.field.recipientState'),
                  data.state
                )}
              {renderDetailRow(
                i18n.getMessage('newPaymentForms.creationConfirmation.field.recipientAddress'),
                data.address
              )}
              {getSectionError(['registrationNumber', 'country', 'city', 'postCode', 'state', 'address']) && (
                <div className={'new-payment-template-confirmation-error-message'}>
                  {getSectionError(['registrationNumber', 'country', 'city', 'postCode', 'state', 'address'])}
                </div>
              )}
            </div>
          </>
        )}
      </div>

      <div className={'new-payment-template-attach-doc-wrapper'}>
        <CustomFileDragAndDrop
          files={newPaymentStore.uploadedFiles}
          onChange={newPaymentStore.uploadDocuments(customerNumber)}
          onRemoveFiles={(fileId) => newPaymentStore.removePaymentFile(fileId)}
          maxSize={MAX_UPLOADED_FILES_SIZE}
          error={
            newPaymentStore.error?.type === 'attachDoc' ? getErrorMessageForAlert(i18n, newPaymentStore.error) : null
          }
          hintText={i18n.getMessage('attachDoc.hint.text', {
            maxFilesSize: convertBytesToMegabytes(MAX_UPLOADED_FILES_SIZE)
          })}
        />
      </div>

      {!newPaymentStore.isShowingConfirmationTab ? (
        <div className={'new-payment-template-confirmation-buttons-wrapper'}>
          <Button
            className={'new-payment-template-confirmation-button'}
            type={'primary'}
            onClick={calculateCommission}
            isDisabled={newPaymentStore.isLoading}
          >
            {i18n.getMessage('newPaymentForms.creationConfirmation.button.calculateCommission')}
          </Button>
        </div>
      ) : (
        <div className={'new-payment-template-confirmation-buttons-wrapper'}>
          <Button
            className={'new-payment-template-confirmation-button '}
            type={'outline'}
            onClick={editPaymentDetails}
            isDisabled={newPaymentStore.isLoading}
          >
            {i18n.getMessage('newPaymentForms.creationConfirmation.button.editPaymentDetails')}
          </Button>
          <Button
            className={'new-payment-template-confirmation-button'}
            type={'primary'}
            onClick={createNewPayment}
            isDisabled={newPaymentStore.isLoading}
          >
            {i18n.getMessage('newPaymentForms.creationConfirmation.button.createPayment')}
          </Button>
        </div>
      )}

      <PopUp
        className={
          newPaymentStore.isSuccess
            ? 'transaction-success'
            : newPaymentStore.isShowingInternalActionConfirmation
              ? 'action-confirmation-popup pop-up-card--transparent'
              : 'transaction-info'
        }
        show={newPaymentStore.isShowingInternalActionConfirmation || newPaymentStore.isSuccess}
        alignOnCenter={newPaymentStore.isSuccess || newPaymentStore.isShowingInternalActionConfirmation}
        onClose={async () => {
          if (newPaymentStore.isSuccess) {
            newPaymentStore.resetSuccess();
            newPaymentStore.resetNewPaymentFormCreationData();
          } else if (newPaymentStore.isShowingInternalActionConfirmation) {
            newPaymentStore.setIsShowingInternalActionConfirmation(false);
            newPaymentStore.setIsShowSwitchConfirmationMethodScreen(false);
          }
        }}
      >
        {newPaymentStore.isSuccess ? (
          <PopUpSuccessScheme
            onClose={() => {
              newPaymentStore.resetSuccess();
              newPaymentStore.resetNewPaymentFormCreationData();
            }}
            message={i18n.getMessage('popUp.message.moneyTransfer')}
          />
        ) : newPaymentStore.isShowingInternalActionConfirmation ? (
          <PopUpInternalActionConfirmationScheme
            isLoading={newPaymentStore.isLoading}
            isShowSwitchConfirmationMethodScreen={newPaymentStore.isShowSwitchConfirmationMethodScreen}
            title={i18n.getMessage('transfer.popUp.actionConfirmation.title')}
            confirmButtonLabel={i18n.getMessage('transfer.popUp.actionConfirmation.button.confirm')}
            onClose={handleCloseInternalActionConfirmation}
            onConfirm={
              isCryptoProvider(newPaymentStore.currentAccount?.transfer_provider) ? verifyCryptoTransfer : verifyPayment
            }
            error={newPaymentStore.confirmationPopupError}
            clearError={() => newPaymentStore.clearConfirmationPopupError()}
            resendSecurityCode={() => resendVerifyCode()}
            setIsShowSwitchConfirmationMethodScreen={(status) =>
              newPaymentStore.setIsShowSwitchConfirmationMethodScreen(status)
            }
            handleForceSwitchToAnotherConfirmationType={newPaymentStore.forceSwitchToAnotherConfirmationType}
            confirmationId={newPaymentStore.confirmationId}
            module={BACKEND_MODULE_TYPES.PAYMENT}
          />
        ) : null}
      </PopUp>
    </div>
  );
};

ReviewAndSaveNewPaymentFormStep.propTypes = {
  customerNumber: PropTypes.string,
  newPaymentStore: MobXPropTypes.observableObject
};

export default inject((stores) => ({
  newPaymentStore: stores.newPaymentStore
}))(observer(ReviewAndSaveNewPaymentFormStep));
